// Default (candidate) site marker styling. A default site keeps its normal,
// opaque icon (so it stays legible on any map background) but adds:
//   1. a white "casing" (halo) behind it, which decouples legibility from the
//      marker's own — often dark — color, and
//   2. a same-color dashed ring drawn on that casing as the "candidate" signal.
// This is a pure SVG transform, so it works on every surface — the DeckGL icon
// atlas, the Admin Geo map icons, and the DOM TreadMarker icons — and fits
// within the original viewBox (no re-anchoring needed by callers).

const FILL_RE = /fill="(#[0-9A-Fa-f]{3,8})"/;
const VIEWBOX_RE = /viewBox="0 0 (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)"/;
const SVG_RE = /(<svg[^>]*>)([\s\S]*)(<\/svg>)/;

/** Scale applied to the icon so the casing + ring fit inside the original canvas. */
const ICON_SCALE = 0.7;

/**
 * Wrap a site marker SVG so it reads as a default (candidate) site: an opaque
 * white casing behind the (scaled-down) icon, plus a same-color dashed ring on
 * the casing. Keeps the icon opaque for legibility on dark backgrounds.
 *
 * @param svg   a full `<svg>…</svg>` marker string (circle + glyph)
 * @param color ring color; defaults to the marker's first `fill` (its disc)
 */
export const withDefaultSiteRing = (svg: string, color?: string): string => {
  const svgMatch = svg.match(SVG_RE);
  if (!svgMatch) return svg;
  const [, open, inner, close] = svgMatch;

  const viewBox = svg.match(VIEWBOX_RE);
  const size = viewBox ? Number(viewBox[1]) : 32;
  const c = size / 2; // center
  const haloR = c - 1; // white casing, just inside the canvas edge
  const ringR = c - 3; // dashed ring sits on the casing, outside the icon
  const ringColor = color ?? svg.match(FILL_RE)?.[1] ?? '#000000';

  const halo =
    `<circle cx="${c}" cy="${c}" r="${haloR.toFixed(1)}" fill="#ffffff" ` +
    `stroke="#cbd5e1" stroke-width="0.75"/>`;
  const scaledIcon =
    `<g transform="translate(${c} ${c}) scale(${ICON_SCALE}) translate(${-c} ${-c})">` +
    inner +
    '</g>';
  const ring =
    `<circle cx="${c}" cy="${c}" r="${ringR.toFixed(1)}" fill="none" ` +
    `stroke="${ringColor}" stroke-width="2" stroke-dasharray="3 2.6" stroke-linecap="round"/>`;

  return `${open}${halo}${scaledIcon}${ring}${close}`;
};
